import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { toApiError, useApi, type BookingResponse } from '@/shared/api';
import { canManage, isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { ActionMenu, ConfirmDialog } from '@/shared/components';
import { onboardingErrorText } from '@/features/system/onboardingErrors';

/**
 * ⋯ einer Aufenthaltskarte, nur für Verwalter. „Begrüßung neu senden“ kann laut Server nur die
 * Verwaltung; vorher fragt ein Dialog, weil die Mail sofort an den Gast geht.
 */
export function BookingCardMenu({ booking, label }: { booking: BookingResponse; label: string }) {
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const recipient = booking.guestName ?? 'den Gast';
  // Stornierte und nicht angereiste Gäste lehnt der Check-in ab, abgereiste der Server.
  const greetable =
    !['Cancelled', 'NoShow'].includes(booking.status) && booking.stayPhase !== 'Completed';
  const resend = useMutation({
    mutationFn: () => api.system.onboardingLinkReIssue(booking.id),
    onSuccess: () => {
      setConfirming(false);
      toast.success(`Die Begrüßung an ${recipient} liegt im Ausgang.`);
    },
    onError: async (error) => {
      const problem = await toApiError(error);
      toast.error(onboardingErrorText(problem, 'Die Begrüßung konnte nicht gesendet werden.'));
    },
  });
  if (!canManage(user)) return null;
  return (
    <>
      <ActionMenu
        align="right"
        className="absolute right-3 top-3 md:top-1/2 md:-translate-y-1/2"
        trigger={
          <button
            type="button"
            aria-label={`Aktionen für ${label}`}
            className="border-hairline rounded-pill text-ink flex size-11 items-center justify-center border bg-white"
          >
            <MoreHorizontal size={17} aria-hidden />
          </button>
        }
        items={[
          {
            label: 'Details öffnen',
            icon: 'ExternalLink',
            onClick: () => void navigate({ to: '/bookings/$id', params: { id: booking.id } }),
          },
          ...(isWorkspaceAdmin(user) && greetable
            ? [
                {
                  label: 'Begrüßung neu senden …',
                  icon: 'Mail' as const,
                  onClick: () => setConfirming(true),
                },
              ]
            : []),
        ]}
      />
      <ConfirmDialog
        open={confirming}
        onOpenChange={(open) => !resend.isPending && setConfirming(open)}
        title="Begrüßung neu senden?"
        description={`Die Begrüßung mit dem Check-in-Link geht noch einmal an ${recipient}. Der bisherige Link bleibt gültig.`}
        confirmLabel="Neu senden"
        variant="brand"
        loading={resend.isPending}
        onConfirm={() => resend.mutate()}
      />
    </>
  );
}
